import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-6">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Terms of Service
        </h1>
        <p className="text-slate-500 mb-12">Last updated: February 2026</p>

        <div className="prose prose-slate max-w-none">
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              1. Agreement to Terms
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              By accessing or using ClientOps ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
            </p>
            <p className="text-slate-600 leading-relaxed">
              These Terms apply to all visitors, users, and others who access or use the Service. By using the Service, you represent that you are at least 18 years of age and have the legal capacity to enter into these Terms.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              2. Description of Service
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              ClientOps is a client operations platform that provides tools for managing client relationships, follow-ups, invoicing, and business organization. The Service includes:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Client relationship management tools</li>
              <li>Follow-up reminders and scheduling</li>
              <li>Invoice generation and payment tracking</li>
              <li>Multi-business management capabilities</li>
              <li>Data import and export functionality</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              3. User Accounts
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              When you create an account with us, you must provide accurate, complete, and current information. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password. You agree not to disclose your password to any third party.
            </p>
            <p className="text-slate-600 leading-relaxed">
              You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              4. Subscription and Billing
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Some parts of the Service are billed on a subscription basis ("Subscription(s)"). You will be billed in advance on a recurring and periodic basis ("Billing Cycle"). Billing cycles are set on a monthly or annual basis.
            </p>
            <p className="text-slate-600 leading-relaxed mb-4">
              At the end of each Billing Cycle, your Subscription will automatically renew under the same conditions unless you cancel it or ClientOps cancels it. You may cancel your Subscription renewal through your account settings or by contacting our support team.
            </p>
            <p className="text-slate-600 leading-relaxed">
              All fees are exclusive of all taxes, levies, or duties imposed by taxing authorities, and you shall be responsible for payment of all such taxes, levies, or duties.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              5. Free Trial
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              ClientOps may offer a free trial period for new users. At the end of the free trial period, your account will be automatically converted to a paid subscription unless you cancel before the trial ends.
            </p>
            <p className="text-slate-600 leading-relaxed">
              We reserve the right to modify or discontinue the free trial offer at any time without notice.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              6. Acceptable Use
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              You agree not to use the Service:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>In any way that violates any applicable national or international law or regulation</li>
              <li>To transmit any unsolicited or unauthorized advertising or promotional material</li>
              <li>To impersonate or attempt to impersonate ClientOps, a ClientOps employee, or another user</li>
              <li>To engage in any conduct that restricts or inhibits anyone's use of the Service</li>
              <li>To introduce any viruses, trojan horses, worms, or other malicious code</li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              7. Intellectual Property
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              The Service and its original content, features, and functionality are and will remain the exclusive property of ClientOps and its licensors. The Service is protected by copyright, trademark, and other laws.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of ClientOps.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              8. Your Data
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              You retain all rights to your data that you upload or input into the Service. By using the Service, you grant ClientOps a license to use, store, and process your data solely for the purpose of providing the Service to you.
            </p>
            <p className="text-slate-600 leading-relaxed">
              You are responsible for maintaining backups of your data. ClientOps is not liable for any loss or corruption of data.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              9. Termination
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We may terminate or suspend your account immediately, without prior notice or liability, for any reason, including without limitation if you breach the Terms.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service or contact us to delete your account.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              10. Limitation of Liability
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              In no event shall ClientOps, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from your use of the Service.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              11. Disclaimer
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Your use of the Service is at your sole risk. The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Service is provided without warranties of any kind, whether express or implied.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              12. Changes to Terms
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              We reserve the right to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect.
            </p>
            <p className="text-slate-600 leading-relaxed">
              By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              13. Contact Us
            </h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions about these Terms, please contact us at{" "}
              <a href="mailto:hello@clientopsapp.com" className="text-blue-600 hover:underline">
                hello@clientopsapp.com
              </a>
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-8">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-slate-500 text-sm">© 2026 ClientOps. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
